import { Avatar, Spin, Typography } from 'antd';
import {
  UserOutlined,
  EditOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ProviderProfile } from '../../../types/provider';
import { useLanguageStore } from '../../../store/languageStore';
import { pickTranslation } from '../../../utils/i18nDisplay';

const { Text } = Typography;

interface ProfileHeroProps {
  profile: ProviderProfile;
  uploading: boolean;
  deleting: boolean;
  onPhotoZoneActivate: () => void;
  onEditProfile: () => void;
}

/**
 * Premium hero card — yashil-to'q ko'k gradient, dekorativ blur orblar,
 * 72px avatar oq border bilan, status dot va xizmat sozlamasi pill'lari.
 * Analytics hero kartochkasiga uslubiy mos.
 */
export function ProfileHero({
  profile,
  uploading,
  deleting,
  onPhotoZoneActivate,
  onEditProfile,
}: ProfileHeroProps) {
  const { t } = useTranslation('provider');
  const { currentLanguage, defaultLanguage } = useLanguageStore();
  const disabled = uploading || deleting;

  const translated = pickTranslation(profile.translations, currentLanguage, defaultLanguage);
  const displayName = translated?.displayName?.trim() || profile.displayName;
  const bio = translated?.bio?.trim() || profile.bio;
  const isActive = profile.active;

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 20,
        marginBottom: 20,
        padding: '20px 18px 18px',
        background:
          'linear-gradient(135deg, #1d4ed8 0%, #2563eb 35%, #3b82f6 70%, #60a5fa 100%)',
        overflow: 'hidden',
        boxShadow: '0 8px 28px rgba(37,99,235,0.32)',
      }}
    >
      {/* Decorative orbital ring 1 */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -70,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)',
          filter: 'blur(8px)',
        }}
      />
      {/* Decorative orbital ring 2 */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -50,
          left: -30,
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(8px)',
        }}
      />
      {/* Subtle grid texture */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(at 20% 30%, rgba(255,255,255,0.12) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        {/* Avatar zone */}
        <div
          role="button"
          tabIndex={0}
          aria-label={t('profile.photo.ariaLabel')}
          aria-busy={disabled}
          onClick={() => !disabled && onPhotoZoneActivate()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              onPhotoZoneActivate();
            }
          }}
          style={{
            position: 'relative',
            width: 72,
            height: 72,
            flexShrink: 0,
            cursor: disabled ? 'progress' : 'pointer',
            borderRadius: '50%',
            outline: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {/* Soft halo */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: -6,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              filter: 'blur(4px)',
            }}
          />
          <Avatar
            size={72}
            src={profile.photoUrl ?? undefined}
            icon={<UserOutlined />}
            style={{
              position: 'relative',
              backgroundColor: 'rgba(255,255,255,0.22)',
              fontSize: 30,
              opacity: disabled ? 0.55 : 1,
              transition: 'opacity 120ms ease-out',
              border: '3px solid rgba(255,255,255,0.42)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
            }}
          />
          {disabled && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Spin size="small" />
            </div>
          )}
          {/* Camera badge */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#fff',
              border: '2px solid #2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              color: '#2563eb',
              boxShadow: '0 2px 6px rgba(0,0,0,0.22)',
            }}
          >
            <CameraOutlined />
          </div>
        </div>

        {/* Name + bio + status */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <Text
              style={{
                fontSize: 19,
                fontWeight: 800,
                color: '#fff',
                letterSpacing: -0.3,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </Text>
            <span
              aria-hidden
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: isActive ? '#34c669' : 'rgba(255,255,255,0.55)',
                boxShadow: isActive ? '0 0 0 3px rgba(52,198,105,0.28)' : 'none',
                flexShrink: 0,
              }}
            />
          </div>
          {bio && (
            <Text
              style={{
                fontSize: 13,
                display: 'block',
                color: 'rgba(255,255,255,0.78)',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {bio}
            </Text>
          )}
        </div>

        {/* Edit button */}
        <button
          type="button"
          aria-label={t('profile.hero.editAriaLabel')}
          onClick={onEditProfile}
          style={{
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.22)',
            borderRadius: 12,
            flexShrink: 0,
            fontSize: 16,
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            transition: 'background 0.15s ease',
          }}
        >
          <EditOutlined />
        </button>
      </div>
    </div>
  );
}
